import {
  BadgeCheck,
  Check,
  Crown,
  DoorOpen,
  PackageOpen,
  Pickaxe,
  Trash2,
  UserPlus,
  UserRound,
  Users,
  UserX,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { type SocialProfile } from "../types";
import { type GuildActions } from "../ui/navigation";

export function SocialPanel({
  profile,
  busy,
  onFriendRequest,
  onFriendRespond,
  onFriendRemove,
  onCrewCreate,
  onCrewJoin,
  onCrewLeave,
  onCrewSupply,
  guildActions,
}: {
  profile: SocialProfile;
  busy: boolean;
  onFriendRequest: (username: string) => void;
  onFriendRespond: (userId: string, accept: boolean) => void;
  onFriendRemove: (userId: string) => void;
  onCrewCreate: (name: string) => void;
  onCrewJoin: (name: string) => void;
  onCrewLeave: () => void;
  onCrewSupply: () => void;
  guildActions: GuildActions;
}) {
  const [tab, setTab] = useState<"friends" | "crew">("friends");
  const [friendName, setFriendName] = useState("");
  const [crewName, setCrewName] = useState("");
  const guild = profile.guild;
  const [announcement, setAnnouncement] = useState(guild?.announcement ?? "");
  const [joinPolicy, setJoinPolicy] = useState<"open" | "approval" | "invite_only">(
    guild?.joinPolicy ?? "open",
  );
  const [guildInviteName, setGuildInviteName] = useState("");
  useEffect(() => {
    setAnnouncement(guild?.announcement ?? "");
    setJoinPolicy(guild?.joinPolicy ?? "open");
  }, [guild?.id, guild?.announcement, guild?.joinPolicy]);
  return (
    <section className="social-panel" aria-label="社交">
      <nav className="social-tabs" aria-label="社交分类">
        <button className={tab === "friends" ? "is-active" : ""} onClick={() => setTab("friends")}>
          <Users size={15} />
          好友
        </button>
        <button className={tab === "crew" ? "is-active" : ""} onClick={() => setTab("crew")}>
          <Pickaxe size={15} />
          公会
        </button>
      </nav>
      {tab === "friends" && (
        <div className="social-pane">
          <form
            className="social-inline-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!friendName.trim()) return;
              onFriendRequest(friendName.trim());
              setFriendName("");
            }}
          >
            <input
              value={friendName}
              onChange={(event) => setFriendName(event.target.value)}
              placeholder="输入用户名"
              maxLength={24}
            />
            <button
              className="icon-button"
              disabled={busy || !friendName.trim()}
              aria-label="添加好友"
              title="添加好友"
            >
              <UserPlus size={17} />
            </button>
          </form>
          {profile.incoming.map((person) => (
            <div className="social-person is-incoming" key={person.userId}>
              <div>
                <strong>{person.nickname}</strong>
                <small>@{person.username}</small>
              </div>
              <button
                className="icon-button"
                disabled={busy}
                onClick={() => onFriendRespond(person.userId, true)}
                aria-label="接受好友"
                title="接受好友"
              >
                <Check size={16} />
              </button>
              <button
                className="icon-button"
                disabled={busy}
                onClick={() => onFriendRespond(person.userId, false)}
                aria-label="拒绝好友"
                title="拒绝好友"
              >
                <X size={16} />
              </button>
            </div>
          ))}
          {profile.friends.length === 0 && profile.incoming.length === 0 ? (
            <p className="social-empty">还没有好友</p>
          ) : (
            profile.friends.map((person) => (
              <div className="social-person" key={person.userId}>
                <span className="member-avatar">
                  <UserRound size={16} />
                </span>
                <div>
                  <strong>{person.nickname}</strong>
                  <small>最深 {person.bestDepth} 层</small>
                </div>
                <button
                  className="icon-button"
                  disabled={busy}
                  onClick={() => onFriendRemove(person.userId)}
                  aria-label="移除好友"
                  title="移除好友"
                >
                  <UserX size={16} />
                </button>
              </div>
            ))
          )}
        </div>
      )}
      {tab === "crew" && (
        <div className="social-pane">
          {!guild &&
            profile.guildInvites?.map((invite) => (
              <div className="social-person is-incoming" key={invite.guildId}>
                <div>
                  <strong>{invite.guildName}</strong>
                  <small>{invite.inviterName} 邀请你加入</small>
                </div>
                <button
                  className="icon-button"
                  disabled={busy}
                  onClick={() => guildActions.respondInvite(invite.guildId, true)}
                  title="接受公会邀请"
                  aria-label="接受公会邀请"
                >
                  <Check size={15} />
                </button>
                <button
                  className="icon-button"
                  disabled={busy}
                  onClick={() => guildActions.respondInvite(invite.guildId, false)}
                  title="拒绝公会邀请"
                  aria-label="拒绝公会邀请"
                >
                  <X size={15} />
                </button>
              </div>
            ))}
          {guild ? (
            <>
              <div className="crew-heading">
                <span>
                  <Crown size={17} />
                </span>
                <div>
                  <strong>{guild.name}</strong>
                  <small>
                    {guild.members.length} / 30 名成员 ·{" "}
                    {guild.yourRole === "owner"
                      ? "会长"
                      : guild.yourRole === "officer"
                        ? "官员"
                        : "成员"}
                  </small>
                </div>
              </div>
              {guild.announcement && <p className="guild-announcement">{guild.announcement}</p>}
              <div className="crew-actions">
                <button className="primary-command compact" disabled={busy} onClick={onCrewSupply}>
                  <PackageOpen size={16} />
                  领取补给
                </button>
                <button
                  className="secondary-command compact"
                  disabled={busy || guild.yourRole === "owner"}
                  onClick={onCrewLeave}
                >
                  <DoorOpen size={16} />
                  离开
                </button>
                {guild.yourRole === "owner" && guild.members.length === 1 && (
                  <button
                    className="secondary-command compact"
                    disabled={busy}
                    onClick={guildActions.archive}
                  >
                    <Trash2 size={16} />
                    解散公会
                  </button>
                )}
              </div>
              {guild.yourRole === "owner" && (
                <div className="guild-settings">
                  <textarea
                    value={announcement}
                    onChange={(event) => setAnnouncement(event.currentTarget.value)}
                    maxLength={160}
                    placeholder="公会公告"
                  />
                  <select
                    value={joinPolicy}
                    onChange={(event) =>
                      setJoinPolicy(event.currentTarget.value as typeof joinPolicy)
                    }
                  >
                    <option value="open">公开加入</option>
                    <option value="approval">申请审批</option>
                    <option value="invite_only">仅邀请</option>
                  </select>
                  <button
                    className="secondary-command compact"
                    disabled={busy}
                    onClick={() => guildActions.update(announcement, joinPolicy)}
                  >
                    <Check size={15} />
                    保存
                  </button>
                </div>
              )}
              {(guild.yourRole === "owner" || guild.yourRole === "officer") && (
                <form
                  className="social-inline-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (guildInviteName.trim()) guildActions.invite(guildInviteName.trim());
                    setGuildInviteName("");
                  }}
                >
                  <input
                    value={guildInviteName}
                    onChange={(event) => setGuildInviteName(event.currentTarget.value)}
                    placeholder="邀请用户名"
                    maxLength={24}
                  />
                  <button
                    className="icon-button"
                    disabled={busy || !guildInviteName.trim()}
                    title="邀请加入公会"
                    aria-label="邀请加入公会"
                  >
                    <UserPlus size={16} />
                  </button>
                </form>
              )}
              {guild.applications?.map((person) => (
                <div className="social-person is-incoming" key={person.userId}>
                  <div>
                    <strong>{person.nickname}</strong>
                    <small>申请加入</small>
                  </div>
                  <button
                    className="icon-button"
                    disabled={busy}
                    onClick={() => guildActions.respondApplication(person.userId, true)}
                    title="批准申请"
                    aria-label="批准申请"
                  >
                    <Check size={15} />
                  </button>
                  <button
                    className="icon-button"
                    disabled={busy}
                    onClick={() => guildActions.respondApplication(person.userId, false)}
                    title="拒绝申请"
                    aria-label="拒绝申请"
                  >
                    <X size={15} />
                  </button>
                </div>
              ))}
              {guild.members.map((person) => (
                <div className="social-person" key={person.userId}>
                  <span className="member-avatar">
                    <UserRound size={16} />
                  </span>
                  <div>
                    <strong>{person.nickname}</strong>
                    <small>
                      {person.status === "owner"
                        ? "会长"
                        : person.status === "officer"
                          ? "官员"
                          : "成员"}{" "}
                      · 最深 {person.bestDepth} 层
                    </small>
                  </div>
                  {guild.yourRole === "owner" && person.status !== "owner" && (
                    <>
                      <button
                        className="icon-button"
                        disabled={busy}
                        onClick={() =>
                          guildActions.setRole(
                            person.userId,
                            person.status === "officer" ? "member" : "officer",
                          )
                        }
                        title={person.status === "officer" ? "降为成员" : "任命官员"}
                        aria-label={person.status === "officer" ? "降为成员" : "任命官员"}
                      >
                        <BadgeCheck size={15} />
                      </button>
                      <button
                        className="icon-button"
                        disabled={busy}
                        onClick={() => guildActions.transfer(person.userId)}
                        title="转让会长"
                        aria-label="转让会长"
                      >
                        <Crown size={15} />
                      </button>
                    </>
                  )}
                  {(guild.yourRole === "owner" ||
                    (guild.yourRole === "officer" && person.status === "member")) &&
                    person.status !== "owner" && (
                      <button
                        className="icon-button"
                        disabled={busy}
                        onClick={() => guildActions.remove(person.userId)}
                        title="移出公会"
                        aria-label="移出公会"
                      >
                        <UserX size={15} />
                      </button>
                    )}
                </div>
              ))}
            </>
          ) : (
            <>
              <form
                className="social-inline-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (crewName.trim()) onCrewJoin(crewName.trim());
                }}
              >
                <input
                  value={crewName}
                  onChange={(event) => setCrewName(event.target.value)}
                  placeholder="公会名称"
                  maxLength={20}
                />
                <button className="secondary-command compact" disabled={busy || !crewName.trim()}>
                  加入/申请
                </button>
              </form>
              <button
                className="primary-command compact social-create-crew"
                disabled={busy || !crewName.trim()}
                onClick={() => onCrewCreate(crewName.trim())}
              >
                <Pickaxe size={16} />
                创建公会
              </button>
            </>
          )}
        </div>
      )}
    </section>
  );
}
